import type { App } from "vue";

import VAccessChip from "./components/AccessChip/VAccessChip.vue";
import VAccordion from "./components/Accordion/VAccordion.vue";
import VAccordionContent from "./components/Accordion/VAccordionContent.vue";
import VAccordionHeader from "./components/Accordion/VAccordionHeader.vue";
import VAccordionPanel from "./components/Accordion/VAccordionPanel.vue";
import ACTooltip from "./components/ACTooltip/ACTooltip.vue";
import VAlert from "./components/Alert/VAlert.vue";
import VModernSelect from "./components/AutoComplete/VModernSelect.vue";
import VBadge from "./components/Badge/VBadge.vue";
import VButton from "./components/Button/VButton.vue";
import VCheckbox from "./components/Checkbox/VCheckbox.vue";
import VCheckboxList from "./components/CheckboxList/VCheckboxList.vue";
import VCheckboxTile from "./components/CheckboxTile/VCheckboxTile.vue";
import VCheckboxTileGroup from "./components/CheckboxTile/VCheckboxTileGroup.vue";
import VColorPicker from "./components/ColorPicker/VColorPicker.vue";
import VDatePicker from "./components/DateTime/VDatePicker.vue";
import VTimePicker from "./components/DateTime/VTimePicker.vue";
import { createDialogPlugin } from "./components/Dialog/createDialogPlugin";
import DialogHost from "./components/Dialog/DialogHost.vue";
import VDialog from "./components/Dialog/VDialog.vue";
import VDialogHeader from "./components/Dialog/VDialogHeader.vue";
import VDialogStepper from "./components/DialogStepper/VDialogStepper.vue";
import VDialogStepperItem from "./components/DialogStepper/VDialogStepperItem.vue";
import VDialogStepperRail from "./components/DialogStepper/VDialogStepperRail.vue";
import VDivider from "./components/Divider/VDivider.vue";
import VDropdown from "./components/Dropdown/VDropdown.vue";
import VDropdownItem from "./components/DropdownItem/VDropdownItem.vue";
import VEmptyState from "./components/EmptyState/VEmptyState.vue";
import VFieldDisclosure from "./components/FieldDisclosure/VFieldDisclosure.vue";
import VFieldLabel from "./components/Form/VFieldLabel.vue";
import VHelpTooltip from "./components/HelpTooltip/VHelpTooltip.vue";
import VIconTile from "./components/IconTile/VIconTile.vue";
import IconTitleText from "./components/IconTitleText/IconTitleText.vue";
import VImageTiles from "./components/ImageTiles/VImageTiles.vue";
import VInput from "./components/Input/VInput.vue";
import VInputNumber from "./components/InputNumber/VInputNumber.vue";
import VListCard from "./components/ListCard/VListCard.vue";
import VListCardRow from "./components/ListCard/VListCardRow.vue";
import VLocaleSelect from "./components/LocaleSelect/VLocaleSelect.vue";
import VMarkdown from "./components/Markdown/VMarkdown.vue";
import VPaginator from "./components/Paginator/VPaginator.vue";
import VPill from "./components/Pill/VPill.vue";
import VPriceSelector from "./components/PriceSelector/VPriceSelector.vue";
import VQuantityDropdown from "./components/QuantityDropdown/VQuantityDropdown.vue";
import VRadio from "./components/Radio/VRadio.vue";
import VRadioTile from "./components/RadioTile/VRadioTile.vue";
import VRadioTileGroup from "./components/RadioTile/VRadioTileGroup.vue";
import VSegmentedControl from "./components/SegmentedControl/VSegmentedControl.vue";
import VSelectButton from "./components/SelectButton/VSelectButton.vue";
import VActionRow from "./components/SettingsSection/VActionRow.vue";
import VSectionLabel from "./components/SettingsSection/VSectionLabel.vue";
import VSettingsSection from "./components/SettingsSection/VSettingsSection.vue";
import VSkeleton from "./components/Skeleton/VSkeleton.vue";
import VSlider from "./components/Slider/VSlider.vue";
import VSpacer from "./components/Spacer/VSpacer.vue";
import VStatusChip from "./components/StatusChip/VStatusChip.vue";
import VSteps from "./components/Steps/VSteps.vue";
import VSwitch from "./components/Switch/VSwitch.vue";
import VSwitchRow from "./components/SwitchRow/VSwitchRow.vue";
import VSwitchRowGroup from "./components/SwitchRow/VSwitchRowGroup.vue";
import VGroupBy from "./components/Table/VGroupBy.vue";
import VMenuSelect from "./components/Table/VMenuSelect.vue";
import VSearch from "./components/Table/VSearch.vue";
import VSort from "./components/Table/VSort.vue";
import VTable from "./components/Table/VTable.vue";
import VTabItem from "./components/Tabs/TabItem/VTabItem.vue";
import VTabList from "./components/Tabs/TabList/VTabList.vue";
import VTabPanel from "./components/Tabs/TabPanel/VTabPanel.vue";
import VTabPanels from "./components/Tabs/TabPanels/VTabPanels.vue";
import VTabs from "./components/Tabs/VTabs.vue";
import VTag from "./components/Tag/VTag.vue";
import VTagGroup from "./components/TagGroup/VTagGroup.vue";
import VTagInput from "./components/TagInput/VTagInput.vue";
import VTextArea from "./components/TextArea/VTextArea.vue";
import VTimeline from "./components/Timeline/VTimeline.vue";
import VTimezoneSelect from "./components/TimezoneSelect/VTimezoneSelect.vue";
import VTooltip from "./components/Tooltip/VTooltip.vue";
import "./global.css";

declare module "vue" {
  export interface GlobalComponents {
    VTabs: typeof import("./components/Tabs/VTabs.vue")["default"]
    VTabItem: typeof import("./components/Tabs/TabItem/VTabItem.vue")["default"]
    VTabList: typeof import("./components/Tabs/TabList/VTabList.vue")["default"]
    VTabPanel: typeof import("./components/Tabs/TabPanel/VTabPanel.vue")["default"]
    VTabPanels: typeof import("./components/Tabs/TabPanels/VTabPanels.vue")["default"]
    VAccordion: typeof import("./components/Accordion/VAccordion.vue")["default"]
    VAccordionContent: typeof import("./components/Accordion/VAccordionContent.vue")["default"]
    VAccordionHeader: typeof import("./components/Accordion/VAccordionHeader.vue")["default"]
    VAccordionPanel: typeof import("./components/Accordion/VAccordionPanel.vue")["default"]
    VModernSelect: typeof import("./components/AutoComplete/VModernSelect.vue")["default"]
    VDatePicker: typeof import("./components/DateTime/VDatePicker.vue")["default"]
    VTimePicker: typeof import("./components/DateTime/VTimePicker.vue")["default"]
    VSort: typeof import("./components/Table/VSort.vue")["default"]
    VSearch: typeof import("./components/Table/VSearch.vue")["default"]
    VGroupBy: typeof import("./components/Table/VGroupBy.vue")["default"]
    VMenuSelect: typeof import("./components/Table/VMenuSelect.vue")["default"]
    VTable: typeof import("./components/Table/VTable.vue")["default"]
    VButton: typeof import("./components/Button/VButton.vue")["default"]
    VBadge: typeof import("./components/Badge/VBadge.vue")["default"]
    VSegmentedControl: typeof import("./components/SegmentedControl/VSegmentedControl.vue")["default"]
    VSelectButton: typeof import("./components/SelectButton/VSelectButton.vue")["default"]
    VCheckbox: typeof import("./components/Checkbox/VCheckbox.vue")["default"]
    VDropdown: typeof import("./components/Dropdown/VDropdown.vue")["default"]
    VDropdownItem: typeof import("./components/DropdownItem/VDropdownItem.vue")["default"]
    VFieldLabel: typeof import("./components/Form/VFieldLabel.vue")["default"]
    VInput: typeof import("./components/Input/VInput.vue")["default"]
    VInputNumber: typeof import("./components/InputNumber/VInputNumber.vue")["default"]
    VPaginator: typeof import("./components/Paginator/VPaginator.vue")["default"]
    VPill: typeof import("./components/Pill/VPill.vue")["default"]
    VPriceSelector: typeof import("./components/PriceSelector/VPriceSelector.vue")["default"]
    VRadio: typeof import("./components/Radio/VRadio.vue")["default"]
    VSpacer: typeof import("./components/Spacer/VSpacer.vue")["default"]
    VSwitch: typeof import("./components/Switch/VSwitch.vue")["default"]
    VTagInput: typeof import("./components/TagInput/VTagInput.vue")["default"]
    VTag: typeof import("./components/Tag/VTag.vue")["default"]
    VDivider: typeof import("./components/Divider/VDivider.vue")["default"]
    VFieldDisclosure: typeof import("./components/FieldDisclosure/VFieldDisclosure.vue")["default"]
    VSkeleton: typeof import("./components/Skeleton/VSkeleton.vue")["default"]
    IconTitleText: typeof import("./components/IconTitleText/IconTitleText.vue")["default"]
    VTooltip: typeof import("./components/Tooltip/VTooltip.vue")["default"]
    VHelpTooltip: typeof import("./components/HelpTooltip/VHelpTooltip.vue")["default"]
    ACTooltip: typeof import("./components/ACTooltip/ACTooltip.vue")["default"]
    VTagGroup: typeof import("./components/TagGroup/VTagGroup.vue")["default"]
    VAlert: typeof import("./components/Alert/VAlert.vue")["default"]
    VTextArea: typeof import("./components/TextArea/VTextArea.vue")["default"]
    VLocaleSelect: typeof import("./components/LocaleSelect/VLocaleSelect.vue")["default"]
    VMarkdown: typeof import("./components/Markdown/VMarkdown.vue")["default"]
    VColorPicker: typeof import("./components/ColorPicker/VColorPicker.vue")["default"]
    VTimeline: typeof import("./components/Timeline/VTimeline.vue")["default"]
    VTimezoneSelect: typeof import("./components/TimezoneSelect/VTimezoneSelect.vue")["default"]
    VDialog: typeof import("./components/Dialog/VDialog.vue")["default"]
    DialogHost: typeof import("./components/Dialog/DialogHost.vue")["default"]
    VSlider: typeof import("./components/Slider/VSlider.vue")["default"]
    VDialogStepper: typeof import("./components/DialogStepper/VDialogStepper.vue")["default"]
    VDialogStepperItem: typeof import("./components/DialogStepper/VDialogStepperItem.vue")["default"]
    VDialogStepperRail: typeof import("./components/DialogStepper/VDialogStepperRail.vue")["default"]
    VSteps: typeof import("./components/Steps/VSteps.vue")["default"]
    VQuantityDropdown: typeof import("./components/QuantityDropdown/VQuantityDropdown.vue")["default"]
    VActionRow: typeof import("./components/SettingsSection/VActionRow.vue")["default"]
    VDialogHeader: typeof import("./components/Dialog/VDialogHeader.vue")["default"]
    VEmptyState: typeof import("./components/EmptyState/VEmptyState.vue")["default"]
    VAccessChip: typeof import("./components/AccessChip/VAccessChip.vue")["default"]
    VIconTile: typeof import("./components/IconTile/VIconTile.vue")["default"]
    VImageTiles: typeof import("./components/ImageTiles/VImageTiles.vue")["default"]
    VRadioTile: typeof import("./components/RadioTile/VRadioTile.vue")["default"]
    VRadioTileGroup: typeof import("./components/RadioTile/VRadioTileGroup.vue")["default"]
    VSectionLabel: typeof import("./components/SettingsSection/VSectionLabel.vue")["default"]
    VSettingsSection: typeof import("./components/SettingsSection/VSettingsSection.vue")["default"]
    VStatusChip: typeof import("./components/StatusChip/VStatusChip.vue")["default"]
    VSwitchRow: typeof import("./components/SwitchRow/VSwitchRow.vue")["default"]
    VSwitchRowGroup: typeof import("./components/SwitchRow/VSwitchRowGroup.vue")["default"]
    VListCard: typeof import("./components/ListCard/VListCard.vue")["default"]
    VListCardRow: typeof import("./components/ListCard/VListCardRow.vue")["default"]
    VCheckboxList: typeof import("./components/CheckboxList/VCheckboxList.vue")["default"]
    VCheckboxTile: typeof import("./components/CheckboxTile/VCheckboxTile.vue")["default"]
    VCheckboxTileGroup: typeof import("./components/CheckboxTile/VCheckboxTileGroup.vue")["default"]
  }
}

export default {
  install(app: App) {
    app.use(createDialogPlugin());
    app.component("DialogHost", DialogHost);
    app.component("VTabs", VTabs);
    app.component("VTabItem", VTabItem);
    app.component("VTabList", VTabList);
    app.component("VTabPanel", VTabPanel);
    app.component("VTabPanels", VTabPanels);
    app.component("VAccordion", VAccordion);
    app.component("VAccordionContent", VAccordionContent);
    app.component("VAccordionHeader", VAccordionHeader);
    app.component("VAccordionPanel", VAccordionPanel);
    app.component("VSlider", VSlider);
    app.component("VDialog", VDialog);
    app.component("VModernSelect", VModernSelect);
    app.component("VDatePicker", VDatePicker);
    app.component("VTimePicker", VTimePicker);
    app.component("VSort", VSort);
    app.component("VSearch", VSearch);
    app.component("VGroupBy", VGroupBy);
    app.component("VMenuSelect", VMenuSelect);
    app.component("VTable", VTable);
    app.component("VButton", VButton);
    app.component("VBadge", VBadge);
    app.component("VSegmentedControl", VSegmentedControl);
    app.component("VSelectButton", VSelectButton);
    app.component("VCheckbox", VCheckbox);
    app.component("VDropdown", VDropdown);
    app.component("VDropdownItem", VDropdownItem);
    app.component("VFieldLabel", VFieldLabel);
    app.component("VInput", VInput);
    app.component("VInputNumber", VInputNumber);
    app.component("VPaginator", VPaginator);
    app.component("VPill", VPill);
    app.component("VPriceSelector", VPriceSelector);
    app.component("VRadio", VRadio);
    app.component("VSpacer", VSpacer);
    app.component("VSwitch", VSwitch);
    app.component("VTagInput", VTagInput);
    app.component("VTag", VTag);
    app.component("VDivider", VDivider);
    app.component("VFieldDisclosure", VFieldDisclosure);
    app.component("VSkeleton", VSkeleton);
    app.component("IconTitleText", IconTitleText);
    app.component("ACTooltip", ACTooltip);
    app.component("VTooltip", VTooltip);
    app.component("VHelpTooltip", VHelpTooltip);
    app.component("VTagGroup", VTagGroup);
    app.component("VAlert", VAlert);
    app.component("VTextArea", VTextArea);
    app.component("VLocaleSelect", VLocaleSelect);
    app.component("VMarkdown", VMarkdown);
    app.component("VColorPicker", VColorPicker);
    app.component("VTimeline", VTimeline);
    app.component("VTimezoneSelect", VTimezoneSelect);
    app.component("VDialogStepper", VDialogStepper);
    app.component("VDialogStepperItem", VDialogStepperItem);
    app.component("VDialogStepperRail", VDialogStepperRail);
    app.component("VSteps", VSteps);
    app.component("VQuantityDropdown", VQuantityDropdown);
    app.component("VActionRow", VActionRow);
    app.component("VDialogHeader", VDialogHeader);
    app.component("VEmptyState", VEmptyState);
    app.component("VAccessChip", VAccessChip);
    app.component("VIconTile", VIconTile);
    app.component("VImageTiles", VImageTiles);
    app.component("VRadioTile", VRadioTile);
    app.component("VRadioTileGroup", VRadioTileGroup);
    app.component("VSectionLabel", VSectionLabel);
    app.component("VSettingsSection", VSettingsSection);
    app.component("VStatusChip", VStatusChip);
    app.component("VSwitchRow", VSwitchRow);
    app.component("VSwitchRowGroup", VSwitchRowGroup);
    app.component("VListCard", VListCard);
    app.component("VListCardRow", VListCardRow);
    app.component("VCheckboxList", VCheckboxList);
    app.component("VCheckboxTile", VCheckboxTile);
    app.component("VCheckboxTileGroup", VCheckboxTileGroup);
  },
};
